import React, { useMemo, useState } from "react";
import { AdminSidebar } from "@/components/admin/AdminSidebar";
import { FishsixParentReviewDetail } from "@/components/admin/FishsixParentReviewDetail";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { SidebarInset, SidebarProvider } from "@/components/ui/sidebar";
import type { Database } from "@/integrations/supabase/types";
import { supabase } from "@/integrations/supabase/client";
import { averageFishsixSurveyScore, parseFishsixV1Responses } from "@/utils/fishsixSurveyAdmin";
import { FISHSIX_SURVEY_VERSION } from "@/utils/fishsixSurveyConstants";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { Eye, Loader2, Search, Star } from "lucide-react";

type ParentReview = Database["public"]["Tables"]["parent_reviews"]["Row"] & {
  student_type?: string | null;
  survey_version?: string | null;
  student_code?: string | null;
  responses?: any;
};

type ReviewsTab = "all" | "fishsix" | "legacy";

function isFishsixV1Row(r: ParentReview): boolean {
  return r.student_type === "fishsix" && r.survey_version === FISHSIX_SURVEY_VERSION && r.responses != null;
}

function isLegacyStarRow(r: ParentReview): boolean {
  return r.rating != null && r.rating >= 1 && r.rating <= 5;
}

function matchesSearch(r: ParentReview, q: string): boolean {
  if (!q.trim()) return true;
  const s = q.toLowerCase();
  return (
    (r.parent_line_name?.toLowerCase().includes(s) ?? false) ||
    r.parent_line_id.toLowerCase().includes(s) ||
    (r.feedback?.toLowerCase().includes(s) ?? false) ||
    (r.student_code?.toLowerCase().includes(s) ?? false) ||
    (r.survey_version?.toLowerCase().includes(s) ?? false)
  );
}

function ReviewTypeBadge({ r }: { r: ParentReview }) {
  if (isFishsixV1Row(r)) {
    return (
      <Badge variant="secondary" className="font-normal">
        Fishsix v1
      </Badge>
    );
  }
  if (isLegacyStarRow(r)) {
    return (
      <Badge variant="outline" className="font-normal">
        รีวิวดาว 1–5
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="font-normal">
      {r.survey_version ?? "แบบสอบถาม"}
    </Badge>
  );
}

function StarsSummary({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          className={`h-3.5 w-3.5 ${i <= rating ? "fill-yellow-400 text-yellow-400" : "text-muted-foreground/25"}`}
        />
      ))}
      <span className="ml-1 text-xs tabular-nums text-muted-foreground">{rating}/5</span>
    </div>
  );
}

function SummaryCell({ r }: { r: ParentReview }) {
  if (isFishsixV1Row(r)) {
    const parsed = parseFishsixV1Responses(r.responses);
    const avg = parsed ? averageFishsixSurveyScore(parsed) : null;
    if (avg != null) {
      return (
        <div className="flex flex-col gap-0.5">
          <span className="text-sm font-medium tabular-nums">{avg}/10 เฉลี่ย</span>
          <span className="text-xs text-muted-foreground">4 หมวดคะแนน</span>
        </div>
      );
    }
    return <span className="text-sm text-muted-foreground">ไม่สามารถอ่านคะแนน</span>;
  }
  if (isLegacyStarRow(r) && r.rating != null) {
    return <StarsSummary rating={r.rating} />;
  }
  if (r.responses != null) {
    return <span className="text-xs text-muted-foreground">ข้อมูลแบบสอบถาม (ดูรายละเอียด)</span>;
  }
  return <span className="text-muted-foreground">—</span>;
}

const AdminParentReviewsPage: React.FC = () => {
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState<ReviewsTab>("all");
  const [detailReview, setDetailReview] = useState<ParentReview | null>(null);

  const { data: reviews = [], isLoading } = useQuery({
    queryKey: ["parent-reviews"],
    queryFn: async () => {
      const { data, error } = await supabase.from("parent_reviews").select("*").order("created_at", { ascending: false });
      if (error) throw error;
      return data as ParentReview[];
    },
  });

  const tabFiltered = useMemo(() => {
    return reviews.filter((r) => {
      if (tab === "fishsix") return isFishsixV1Row(r);
      if (tab === "legacy") return isLegacyStarRow(r);
      return true;
    });
  }, [reviews, tab]);

  const filtered = useMemo(() => tabFiltered.filter((r) => matchesSearch(r, search)), [tabFiltered, search]);

  const fishsixCount = useMemo(() => reviews.filter(isFishsixV1Row).length, [reviews]);
  const legacyCount = useMemo(() => reviews.filter(isLegacyStarRow).length, [reviews]);

  const detailParsedFishsix = detailReview ? parseFishsixV1Responses(detailReview.responses) : null;
  const detailShowFishsix =
    detailReview != null && isFishsixV1Row(detailReview) && detailParsedFishsix != null;
  const detailLegacy = detailReview != null && isLegacyStarRow(detailReview);

  return (
    <SidebarProvider>
      <div className="flex min-h-screen w-full">
        <AdminSidebar />
        <SidebarInset>
          <div className="container mx-auto max-w-6xl px-4 py-8">
            <div className="mb-6 flex flex-col gap-2">
              <h1 className="text-2xl font-bold tracking-tight">รีวิวจากผู้ปกครอง</h1>
              <p className="text-sm text-muted-foreground">
                ดูแบบสอบถามความพึงพอใจ Fishsix (เวอร์ชัน {FISHSIX_SURVEY_VERSION}) และรีวิวแบบดาวเดิมในที่เดียว
              </p>
            </div>

            <div
              className="mb-6 inline-flex h-10 w-full max-w-lg items-center justify-stretch rounded-lg border bg-muted/50 p-1"
              role="group"
              aria-label="กรองประเภทรีวิว"
            >
              {(
                [
                  { key: "all" as const, label: `ทั้งหมด (${reviews.length})` },
                  { key: "fishsix" as const, label: `Fishsix (${fishsixCount})` },
                  { key: "legacy" as const, label: `ดาว 1–5 (${legacyCount})` },
                ] as const
              ).map(({ key, label }) => (
                <Button
                  key={key}
                  type="button"
                  variant={tab === key ? "default" : "ghost"}
                  size="sm"
                  className="flex-1 rounded-md text-xs shadow-none sm:text-sm"
                  onClick={() => setTab(key)}
                >
                  {label}
                </Button>
              ))}
            </div>

            <div className="relative mb-4 max-w-md">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="ค้นหา ชื่อ Line, Line ID, รหัสนักเรียน, เวอร์ชันแบบสอบถาม..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>

            <div className="overflow-hidden rounded-xl border bg-card shadow-sm">
              {isLoading ? (
                <div className="flex items-center justify-center py-16">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : filtered.length === 0 ? (
                <div className="py-16 text-center text-muted-foreground">ไม่พบข้อมูลในแท็บนี้</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[720px] text-sm">
                    <thead>
                      <tr className="border-b bg-muted/40">
                        <th className="px-4 py-3 text-left font-medium">ประเภท</th>
                        <th className="px-4 py-3 text-left font-medium">ชื่อ Line</th>
                        <th className="px-4 py-3 text-left font-medium">Line ID</th>
                        <th className="px-4 py-3 text-left font-medium">รหัสนักเรียน</th>
                        <th className="px-4 py-3 text-left font-medium">สรุป</th>
                        <th className="px-4 py-3 text-left font-medium whitespace-nowrap">วันที่</th>
                        <th className="px-4 py-3 text-right font-medium">รายละเอียด</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((r) => (
                        <tr key={r.id} className="border-b border-border/60 transition-colors last:border-0 hover:bg-muted/20">
                          <td className="px-4 py-3 align-middle">
                            <ReviewTypeBadge r={r} />
                          </td>
                          <td className="max-w-[140px] truncate px-4 py-3 align-middle font-medium">{r.parent_line_name || "—"}</td>
                          <td className="max-w-[120px] truncate px-4 py-3 align-middle font-mono text-xs text-muted-foreground">
                            {r.parent_line_id}
                          </td>
                          <td className="px-4 py-3 align-middle font-mono text-xs">{r.student_code || "—"}</td>
                          <td className="max-w-[200px] px-4 py-3 align-middle">
                            <SummaryCell r={r} />
                          </td>
                          <td className="whitespace-nowrap px-4 py-3 align-middle text-muted-foreground">
                            {r.created_at ? format(new Date(r.created_at), "dd/MM/yyyy HH:mm") : "—"}
                          </td>
                          <td className="px-4 py-3 text-right align-middle">
                            <Button type="button" variant="outline" size="sm" className="gap-1.5" onClick={() => setDetailReview(r)}>
                              <Eye className="h-3.5 w-3.5" />
                              เปิด
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <p className="mt-3 text-xs text-muted-foreground">
              แสดง {filtered.length} รายการ
              {tab !== "all" ? ` (จากทั้งหมด ${reviews.length})` : ""}
            </p>
          </div>
        </SidebarInset>
      </div>

      <Sheet open={detailReview != null} onOpenChange={(open) => !open && setDetailReview(null)}>
        <SheetContent side="right" className="flex w-full flex-col overflow-y-auto sm:max-w-2xl">
          {detailReview ? (
            <>
              <SheetHeader className="space-y-3 border-b pb-4 text-left">
                <SheetTitle className="pr-8 text-xl leading-snug">
                  {detailReview.parent_line_name || "ผู้ปกครอง"}
                </SheetTitle>
                <SheetDescription asChild>
                  <div className="space-y-2 text-left text-sm text-muted-foreground">
                    <div className="flex flex-wrap gap-2">
                      <ReviewTypeBadge r={detailReview} />
                      {detailReview.survey_version ? (
                        <Badge variant="outline" className="font-mono font-normal">
                          {detailReview.survey_version}
                        </Badge>
                      ) : null}
                      {detailReview.student_type ? (
                        <Badge variant="outline" className="font-normal">
                          {detailReview.student_type}
                        </Badge>
                      ) : null}
                    </div>
                    <dl className="grid gap-1 text-xs sm:text-sm">
                      <div className="flex gap-2">
                        <dt className="min-w-[7rem] shrink-0 text-muted-foreground">Line ID</dt>
                        <dd className="font-mono text-foreground">{detailReview.parent_line_id}</dd>
                      </div>
                      {detailReview.student_code ? (
                        <div className="flex gap-2">
                          <dt className="min-w-[7rem] shrink-0 text-muted-foreground">รหัสนักเรียน</dt>
                          <dd className="font-mono text-foreground">{detailReview.student_code}</dd>
                        </div>
                      ) : null}
                      <div className="flex gap-2">
                        <dt className="min-w-[7rem] shrink-0 text-muted-foreground">บันทึกเมื่อ</dt>
                        <dd className="text-foreground">
                          {detailReview.created_at ? format(new Date(detailReview.created_at), "dd/MM/yyyy HH:mm") : "—"}
                        </dd>
                      </div>
                    </dl>
                  </div>
                </SheetDescription>
              </SheetHeader>

              {detailShowFishsix && detailParsedFishsix ? (
                <FishsixParentReviewDetail parsed={detailParsedFishsix} />
              ) : detailLegacy ? (
                <div className="mt-6 space-y-6 pb-8">
                  <div>
                    <p className="mb-2 text-sm font-medium text-foreground">คะแนนดาว</p>
                    {detailReview.rating != null ? (
                      <div className="flex items-center gap-2 rounded-lg border bg-muted/30 px-4 py-3">
                        <StarsSummary rating={detailReview.rating} />
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">—</p>
                    )}
                  </div>
                  <div>
                    <p className="mb-2 text-sm font-medium text-foreground">ความคิดเห็น</p>
                    {detailReview.feedback?.trim() ? (
                      <div className="rounded-lg border bg-muted/20 px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap">
                        {detailReview.feedback}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">ไม่มีข้อความ</p>
                    )}
                  </div>
                </div>
              ) : (
                <div className="mt-6 space-y-4 pb-8">
                  <p className="text-sm font-medium text-foreground">ข้อมูลแบบสอบถาม (ดิบ)</p>
                  <pre className="max-h-[60vh] overflow-auto rounded-lg border bg-muted/40 p-4 text-xs leading-relaxed">
                    {detailReview.responses != null
                      ? JSON.stringify(detailReview.responses, null, 2)
                      : "ไม่มี responses"}
                  </pre>
                  {detailReview.feedback?.trim() ? (
                    <div>
                      <p className="mb-2 text-sm font-medium text-foreground">feedback (ถ้ามี)</p>
                      <div className="rounded-lg border bg-muted/20 px-4 py-3 text-sm whitespace-pre-wrap">{detailReview.feedback}</div>
                    </div>
                  ) : null}
                </div>
              )}
            </>
          ) : null}
        </SheetContent>
      </Sheet>
    </SidebarProvider>
  );
};

export default AdminParentReviewsPage;
